import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { MONO_FONT } from '../../../theme/muiTheme';
import { formatKwh, formatNumber, formatTonne } from '../../../shared/utils/number';

function TrendTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const value = payload[0].value;
  const formatted = unit === 'kWh'
    ? formatKwh(value, { withUnit: true })
    : formatTonne(value, { withUnit: true });

  return (
    <Paper elevation={3} sx={{ minWidth: 150, p: 1.25 }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
        <Box sx={{ bgcolor: payload[0].color, borderRadius: '2px', height: 8, width: 8 }} />
        <Typography variant="caption" sx={{ fontFamily: MONO_FONT, fontWeight: 750 }}>
          {formatted}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function ScopeTrendChart({
  data, color, unit, chartId, ariaLabel, dataKey = 'value', fillAvailableHeight = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const maxValue = Math.max(...data.map((item) => item[dataKey]), 0);
  const chartData = data.map((item) => ({
    ...item,
    month: t(`dashboard.months.${item.monthKey}`),
  }));
  const formatAxis = (value) => {
    if (Math.abs(value) >= 1000) return `${formatNumber(value / 1000, { max: 1 })}k`;
    if (maxValue < 20) return formatNumber(value, { max: 1 });
    return formatNumber(value, { max: 0 });
  };
  const formatBarLabel = (value) => formatTonne(value);

  return (
    <Box
      role="img"
      aria-label={ariaLabel}
      sx={{
        flex: fillAvailableHeight ? { lg: '1 1 auto' } : undefined,
        height: fillAvailableHeight
          ? { xs: 280, md: 310, lg: 'auto' }
          : { xs: 280, md: 310 },
        minHeight: fillAvailableHeight ? { lg: 310 } : undefined,
        minWidth: 0,
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: compact ? 8 : 24, right: compact ? 0 : 12, left: 0, bottom: 0 }}
          barCategoryGap={compact ? '18%' : '28%'}
        >
          <defs>
            <linearGradient id={chartId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.78} />
              <stop offset="100%" stopColor={color} stopOpacity={1} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="month"
            axisLine={{ stroke: theme.palette.divider }}
            interval={0}
            tick={{ fill: theme.palette.text.secondary, fontSize: compact ? 9 : 11 }}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
            tickFormatter={formatAxis}
            tickLine={false}
            width={compact ? 42 : 54}
          />
          <Tooltip
            cursor={{ fill: theme.palette.action.hover }}
            content={<TrendTooltip unit={unit} />}
          />
          <Bar dataKey={dataKey} fill={`url(#${chartId})`} maxBarSize={56} radius={[5, 5, 0, 0]}>
            {!compact && (
              <LabelList
                dataKey={dataKey}
                position="top"
                offset={6}
                formatter={formatBarLabel}
                style={{ fill: theme.palette.text.secondary, fontSize: 10, fontWeight: 700 }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
